/**
 * BentoGrid — 12-column responsive layout container for the dashboard.
 *
 * Design rules (uncodixify-ui.md):
 *   - No hover lift, no shadow escalation.
 *   - Children control their own col-span via the `span` prop.
 *
 * RTL rules (rtl-support-arabic.md):
 *   - Grid auto-reverses under dir="rtl"; logical properties only in children.
 *
 * Container queries (Prompt 16):
 *   - Each BentoItem slot is wrapped with `@container` so children can use
 *     Tailwind v4's native `@sm:`, `@md:`, `@lg:` variants to respond to
 *     the SLOT width rather than the viewport width.
 *   - Tailwind v4 ships container query support natively — no plugin needed.
 *   - Container breakpoints: @xs 320px · @sm 480px · @md 640px · @lg 768px
 */

import type { ReactNode, CSSProperties } from "react";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** Responsive column span per breakpoint (out of 12 columns). */
export interface BentoSpan {
  /** Mobile default — usually 12 (full-width). */
  base?: number;
  /** ≥640 px */
  sm?: number;
  /** ≥768 px */
  md?: number;
  /** ≥1024 px */
  lg?: number;
  /** ≥1280 px */
  xl?: number;
}

export interface BentoItemProps {
  children: ReactNode;
  /** Responsive col-span config. Defaults to full-width on all breakpoints. */
  span?: BentoSpan;
  /** Additional class names applied to the slot wrapper. */
  className?: string;
  /** ARIA label for the slot when it contains a landmark. */
  "aria-label"?: string;
  /** Forwarded `id` for skip-link targets. */
  id?: string;
}

export interface BentoGridProps {
  children: ReactNode;
  /** Additional class names applied to the grid root. */
  className?: string;
  /** Override gap (default: gap-4 md:gap-6). */
  gap?: "sm" | "md" | "lg";
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const COL_SPAN: Record<number, string> = {
  1: "col-span-1",
  2: "col-span-2",
  3: "col-span-3",
  4: "col-span-4",
  5: "col-span-5",
  6: "col-span-6",
  7: "col-span-7",
  8: "col-span-8",
  9: "col-span-9",
  10: "col-span-10",
  11: "col-span-11",
  12: "col-span-12",
};

const SM_COL_SPAN: Record<number, string> = {
  1: "sm:col-span-1",
  2: "sm:col-span-2",
  3: "sm:col-span-3",
  4: "sm:col-span-4",
  5: "sm:col-span-5",
  6: "sm:col-span-6",
  7: "sm:col-span-7",
  8: "sm:col-span-8",
  9: "sm:col-span-9",
  10: "sm:col-span-10",
  11: "sm:col-span-11",
  12: "sm:col-span-12",
};

const MD_COL_SPAN: Record<number, string> = {
  1: "md:col-span-1",
  2: "md:col-span-2",
  3: "md:col-span-3",
  4: "md:col-span-4",
  5: "md:col-span-5",
  6: "md:col-span-6",
  7: "md:col-span-7",
  8: "md:col-span-8",
  9: "md:col-span-9",
  10: "md:col-span-10",
  11: "md:col-span-11",
  12: "md:col-span-12",
};

const LG_COL_SPAN: Record<number, string> = {
  1: "lg:col-span-1",
  2: "lg:col-span-2",
  3: "lg:col-span-3",
  4: "lg:col-span-4",
  5: "lg:col-span-5",
  6: "lg:col-span-6",
  7: "lg:col-span-7",
  8: "lg:col-span-8",
  9: "lg:col-span-9",
  10: "lg:col-span-10",
  11: "lg:col-span-11",
  12: "lg:col-span-12",
};

const XL_COL_SPAN: Record<number, string> = {
  1: "xl:col-span-1",
  2: "xl:col-span-2",
  3: "xl:col-span-3",
  4: "xl:col-span-4",
  5: "xl:col-span-5",
  6: "xl:col-span-6",
  7: "xl:col-span-7",
  8: "xl:col-span-8",
  9: "xl:col-span-9",
  10: "xl:col-span-10",
  11: "xl:col-span-11",
  12: "xl:col-span-12",
};

function spanClasses(span: BentoSpan = {}): string {
  const parts: string[] = [];
  const base = span.base ?? 12;
  parts.push(COL_SPAN[base] ?? "col-span-12");
  if (span.sm !== undefined) parts.push(SM_COL_SPAN[span.sm] ?? `sm:col-span-${span.sm}`);
  if (span.md !== undefined) parts.push(MD_COL_SPAN[span.md] ?? `md:col-span-${span.md}`);
  if (span.lg !== undefined) parts.push(LG_COL_SPAN[span.lg] ?? `lg:col-span-${span.lg}`);
  if (span.xl !== undefined) parts.push(XL_COL_SPAN[span.xl] ?? `xl:col-span-${span.xl}`);
  return parts.join(" ");
}

const GAP_CLASS: Record<string, string> = {
  sm: "gap-2 md:gap-3",
  md: "gap-4 md:gap-6",
  lg: "gap-6 md:gap-8",
};

// ---------------------------------------------------------------------------
// Components
// ---------------------------------------------------------------------------

/**
 * Slot wrapper for a single Bento cell.
 * Each slot is marked `@container` so its children can use `@sm:`, `@md:`, `@lg:`
 * Tailwind v4 container-query variants to respond to the slot width, not viewport.
 */
export function BentoItem({
  children,
  span,
  className = "",
  "aria-label": ariaLabel,
  id,
}: BentoItemProps) {
  return (
    <div
      id={id}
      aria-label={ariaLabel}
      className={["@container", spanClasses(span), className].filter(Boolean).join(" ")}
    >
      {children}
    </div>
  );
}

/**
 * Root 12-column grid container.
 *
 * Usage:
 * ```tsx
 * <BentoGrid>
 *   <BentoItem span={{ base: 12, md: 8 }}>Wide widget</BentoItem>
 *   <BentoItem span={{ base: 12, md: 4 }}>Narrow widget</BentoItem>
 * </BentoGrid>
 * ```
 */
export function BentoGrid({ children, className = "", gap = "md" }: BentoGridProps) {
  const gridStyle: CSSProperties = {
    display: "grid",
    gridTemplateColumns: "repeat(12, minmax(0, 1fr))",
  };

  return (
    <div
      style={gridStyle}
      className={[GAP_CLASS[gap], className].filter(Boolean).join(" ")}
      data-testid="bento-grid"
    >
      {children}
    </div>
  );
}
